import { useCallback } from "react";
import { getFieldPlaceholder } from "../utils/fieldTypes";
import { getFieldConfig } from "../utils/fieldConfig";

const FieldManager = ({
  selectedFieldType,
  placedFields,
  setPlacedFields,
  pageNumber,
  scale,
  isPdfLoading,
  onFieldPlaced,
}) => {
  const handleRightColumnClick = (e, currentScale) => {
    if (!selectedFieldType || isPdfLoading) return;

    const fieldConfig = getFieldConfig({
      type: selectedFieldType.type,
      label: selectedFieldType.label,
    });

    if (fieldConfig?.fieldLimit) {
      const currentFields = placedFields.filter(
        (field) =>
          field.type === selectedFieldType.type ||
          field.label === selectedFieldType.label,
      );

      if (currentFields.length >= fieldConfig.fieldLimit) {
        alert(
          `Maximum limit of ${fieldConfig.fieldLimit} fields reached for ${selectedFieldType.label}`,
        );
        onFieldPlaced(null);
        return;
      }
    }

    const wrapper = e.currentTarget.querySelector(".pdf-page-wrapper");
    const pdfContainer = wrapper?.querySelector(".react-pdf__Page");
    const placedField = e.target.closest(".placed-field");

    if (!pdfContainer || (!pdfContainer.contains(e.target) && !placedField)) {
      return;
    }

    const rect = pdfContainer.getBoundingClientRect();
    const actualScale = currentScale || scale;

    // Calculate raw click position relative to PDF container
    let rawX = e.clientX - rect.left;
    let rawY = e.clientY - rect.top;

    const dimensions = fieldConfig?.dimensions || "275x40";
    const widthPx =
      parseInt(
        dimensions.includes("px") ? dimensions : dimensions.split("x")[0],
        10,
      ) || 0;

    // Calculate bounds accounting for field dimensions (no scale division since container is not scaled)
    const maxX = Math.max(0, rect.width / actualScale - widthPx);
    const fieldHeight = selectedFieldType?.dimensions?.includes("x")
      ? parseInt(selectedFieldType.dimensions.split("x")[1])
      : 40;
    const maxY = Math.max(0, rect.height / actualScale - fieldHeight);

    // Apply zoom correction to coordinates
    rawX = Math.min(Math.max(0, rawX), rect.width);
    rawY = Math.min(Math.max(0, rawY), rect.height);

    const normalizedX = rawX / actualScale;
    const normalizedY = rawY / actualScale;

    // Apply position correction for radio and checkbox fields when zooming from 140% to 100%
    let correctedX = normalizedX;
    let correctedY = normalizedY;

    if (
      (selectedFieldType.type === "Checkbox" ||
        selectedFieldType.type === "Radio") &&
      actualScale === 1.4
    ) {
      correctedX = normalizedX - 15;
      correctedY = normalizedY - 15;
    }

    // Ensure field stays within bounds after zoom correction
    const finalX = Math.min(correctedX, maxX);
    const finalY = Math.min(correctedY, maxY);

    const newField = {
      id: Math.random().toString(36).substr(2, 9),
      type: selectedFieldType.type,
      label: selectedFieldType.label,
      inputType: selectedFieldType.inputType,
      x: finalX,
      y: finalY,
      pageNumber: pageNumber,
      value: "",
      placeholder: getFieldPlaceholder(selectedFieldType.type),
      width: widthPx,
      height: selectedFieldType?.dimensions?.includes("x")
        ? parseInt(selectedFieldType.dimensions.split("x")[1])
        : 40,
      fontSize: 14,
      scale: 1,
      needed: false,
      radio_group: "", // Initialize radio_group for radio buttons
    };

    setPlacedFields([...placedFields, newField]);
    onFieldPlaced(null);
  };

  const handleDeleteField = (fieldId) => {
    // This will be handled by the parent component
    return fieldId;
  };

  const handleDuplicateField = (field) => {
    const fieldConfig = getFieldConfig(field);

    if (fieldConfig?.fieldLimit) {
      const currentFields = placedFields.filter((f) => {
        return f.type === field.type;
      });

      if (currentFields.length >= fieldConfig.fieldLimit) {
        const fieldDisplayName = fieldConfig.label || field.type || "field";
        alert(
          `Maximum limit of ${fieldConfig.fieldLimit} fields reached for ${fieldDisplayName}`,
        );
        return;
      }
    }

    const dimensions = fieldConfig?.dimensions || "275x40";
    const width =
      field.width ||
      (dimensions.includes("px")
        ? parseInt(dimensions, 10)
        : parseInt(dimensions.split("x")[0], 10));
    const height =
      field.height ||
      (dimensions.includes("x") ? parseInt(dimensions.split("x")[1], 10) : 40);

    const GAP = 12;

    let newX = field.x;
    let newY = field.y + height + GAP;

    const pdfContainer = document.querySelector(
      ".pdf-page-wrapper .react-pdf__Page",
    );

    if (pdfContainer) {
      const rect = pdfContainer.getBoundingClientRect();
      const containerW = rect.width / scale;
      const containerH = rect.height / scale;
      const maxX = Math.max(0, containerW - width);
      const maxY = Math.max(0, containerH - height);

      const fitsBelow = newY + height <= containerH + 0.5;
      if (!fitsBelow) {
        newX = field.x + width + GAP;
        newY = field.y;
      }

      newX = Math.min(Math.max(0, newX), maxX);
      newY = Math.min(Math.max(0, newY), maxY);
    }

    const duplicateField = {
      ...field,
      id: Math.random().toString(36).substr(2, 9),
      x: newX,
      y: newY,
      pageNumber: field.pageNumber,
      value: "",
      placeholder: field.placeholder || getFieldPlaceholder(field.type),
    };

    setPlacedFields([...placedFields, duplicateField]);
  };

  const handleFieldChange = (fieldId, newValue) => {
    setPlacedFields((prevFields) => {
      const updatedFields = prevFields.map((field) => {
        if (field.id === fieldId) {
          return { ...field, value: newValue };
        }
        return field;
      });

      // Handle radio button grouping - same logic as TaskViewPage
      const changedField = updatedFields.find((field) => field.id === fieldId);
      if (
        changedField &&
        (changedField.type === "radio" || changedField.type === "Radio") &&
        newValue === true
      ) {
        // Clear other radio buttons in the same group
        const radioGroupName = changedField.radio_group
          ? `radio-group-${changedField.radio_group}`
          : `radio-page-${changedField.pageNumber || "default"}`;

        return updatedFields.map((field) => {
          if (
            field.id !== fieldId &&
            (field.type === "radio" || field.type === "Radio")
          ) {
            const fieldRadioGroupName = field.radio_group
              ? `radio-group-${field.radio_group}`
              : `radio-page-${field.pageNumber || "default"}`;
            if (fieldRadioGroupName === radioGroupName) {
              return { ...field, value: false };
            }
          }
          return field;
        });
      }

      return updatedFields;
    });
  };

  const updateField = (updatedField) => {
    setPlacedFields((prevFields) => {
      const newFields = prevFields.map((field) =>
        field.id === updatedField.id ? updatedField : field,
      );
      return newFields;
    });
  };

  // Function to constrain all fields within PDF boundaries
  const constrainFieldsToBoundaries = useCallback(
    (currentScale) => {
      const pdfContainer = document.querySelector(
        ".pdf-page-wrapper .react-pdf__Page",
      );

      if (!pdfContainer) return;

      const rect = pdfContainer.getBoundingClientRect();
      const containerWidth = rect.width / currentScale;
      const containerHeight = rect.height / currentScale;

      setPlacedFields((prevFields) =>
        prevFields.map((field) => {
          // Field dimensions are already stored at base scale, no need to divide by scale
          const fieldWidth = field.width || 100;
          const fieldHeight = field.height || 40;

          // Calculate maximum allowed positions
          const maxX = Math.max(0, containerWidth - fieldWidth);
          const maxY = Math.max(0, containerHeight - fieldHeight);

          // Constrain field position within bounds
          const constrainedX = Math.min(Math.max(0, field.x), maxX);
          const constrainedY = Math.min(Math.max(0, field.y), maxY);

          // Only update if position changed
          if (field.x !== constrainedX || field.y !== constrainedY) {
            return { ...field, x: constrainedX, y: constrainedY };
          }

          return field;
        }),
      );
    },
    [setPlacedFields],
  );

  return {
    handleRightColumnClick,
    handleDeleteField,
    handleDuplicateField,
    handleFieldChange,
    updateField,
    constrainFieldsToBoundaries,
  };
};

export default FieldManager;
